import React from 'react';
import cn from 'classnames';

import { Button, Typography } from '..';
import { BackButton } from '~/Components';
import { Route } from '~/Constants/Routes.constants';
import { VERTICAL_SEPARATOR, WHITE } from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGER, FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';
import {
  IPrivateLayoutTitleWithAction,
  PrivateLayoutTitleWithAction,
  PrivateLayoutOptions,
} from '~/Layout/PrivateLayout';
import { PrivateLayoutOptionsMenu } from '~/Layout/PrivateLayout/PrivateLayoutTitle/PrivateLayoutOptionsMenu';
import { usePrivateLayoutTitle } from '~/Layout/PrivateLayout/PrivateLayoutTitle/usePrivateLayoutTitle';

const { HOME } = Route;

interface ITopToolbarProps {
  title: string | IPrivateLayoutTitleWithAction;
  action?: string;
  actionDisabled?: boolean;
  onActionTaken?: () => void;
  backRoute?: string;
  hideBackButton?: boolean;
  onBack?: () => void;
  cancelInsteadOfBack?: boolean;
  options?: PrivateLayoutOptions[];
}

const useStyles = makeStyles((theme) => ({
  TopToolbarContainer: {
    width: '100%',
    minHeight: 53,
    padding: `0 ${theme.spacing(1)}`,
    borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
    background: WHITE,
    display: 'grid',
    gridTemplateColumns: '0.75fr 2.5fr 0.75fr',
    gridTemplateRows: '1fr',
    gridTemplateAreas: '"back title action"',
    alignItems: 'center',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      height: 'unset',
      padding: '30px 40px 15px',
    },
  },
  TopToolbarTitleFullSize: {
    gridTemplateColumns: 'initial',
  },
  TopToolbarBackButton: { gridArea: 'back', lineHeight: 0 },
  TopToolbarTitle: {
    textAlign: 'center',
    gridArea: 'title',
    fontSize: '18px',
    fontWeight: 'bold',
    letterSpacing: '0.64px',
    lineHeight: '24px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    [theme.breakpoints.up('md')]: {
      fontSize: FONT_SIZE_LARGER,
      fontWeight: 'bolder',
      lineHeight: '34px',
    },
  },
  TopToolbarAction: {
    gridArea: 'action',
    fontSize: FONT_SIZE_SMALLISH,
  },
}));

export const TopToolbar = ({
  title,
  action,
  onActionTaken,
  actionDisabled,
  backRoute,
  hideBackButton,
  onBack,
  cancelInsteadOfBack,
  options,
}: ITopToolbarProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { anchorEl, handleCloseMenu, handleMountMenu } = usePrivateLayoutTitle();

  return (
    <div
      className={cn(classes.TopToolbarContainer, {
        [classes.TopToolbarTitleFullSize]: hideBackButton && !action,
      })}
    >
      {!hideBackButton && (
        <div className={classes.TopToolbarBackButton}>
          <BackButton
            to={backRoute || HOME}
            onBack={onBack}
            text={cancelInsteadOfBack ? t('app.cancel') : undefined}
          />
        </div>
      )}
      <Typography className={classes.TopToolbarTitle}>
        {typeof title === 'string' ? title : <PrivateLayoutTitleWithAction {...title} />}
      </Typography>
      {action && (
        <Button
          onClick={onActionTaken}
          disabled={actionDisabled}
          className={classes.TopToolbarAction}
        >
          {action}
        </Button>
      )}
      {options && (
        <>
          <Button
            onClick={handleMountMenu}
            disabled={actionDisabled}
            className={classes.TopToolbarAction}
          >
            {t('app.options')}
          </Button>
          <PrivateLayoutOptionsMenu
            anchorEl={anchorEl}
            handleCloseMenu={handleCloseMenu}
            options={options}
          />
        </>
      )}
    </div>
  );
};
